import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    type: String,
    data: Object,
    options: { type: Object, default: {} }
  }
  
  static targets = ["canvas"]
  
  // Chart instance variable
  chart = null

  async connect() {
    // Check if we have the necessary data
    if (!this.hasTypeValue || !this.hasDataValue) {
      console.warn("Chart missing required values (type or data)")
      return
    }

    // Lazy-load Chart.js from CDN if not already available
    if (typeof window.Chart === 'undefined') {
      try {
        await this.loadChartJs()
      } catch (error) {
        console.error("Failed to load Chart.js:", error)
        return
      }
    }

    // Initialize the chart
    this.initializeChart()
  }

  loadChartJs() {
    // Reuse pending load if another chart instance already started it
    if (!window._chartJsLoadPromise) {
      window._chartJsLoadPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script')
        script.src = 'https://cdn.jsdelivr.net/npm/chart.js@4/dist/chart.umd.min.js'
        script.onload = resolve
        script.onerror = () => reject(new Error('Chart.js CDN load failed'))
        document.head.appendChild(script)
      })
    }
    return window._chartJsLoadPromise
  }
  
  disconnect() {
    // Clean up chart instance when the controller disconnects to prevent memory leaks
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
  }
  
  // Initialize Chart.js with the provided values
  initializeChart() {
    if (!this.hasCanvasTarget) {
      console.error("No canvas target found for chart")
      return
    }
    
    // Get a context from the canvas element
    const ctx = this.canvasTarget.getContext('2d')
    
    // Initialize the chart with our data
    try {
      this.chart = new Chart(ctx, {
        type: this.typeValue,
        data: this.dataValue,
        options: this.optionsValue
      })
    } catch (error) {
      console.error('Error initializing chart:', error)
    }
  }
  
  // Action to refresh the chart with new data
  refresh(event) {
    if (!this.chart) {
      this.initializeChart()
      return
    }
    
    // Update chart data
    this.chart.data = this.dataValue
    
    // Update chart options if needed
    if (this.hasOptionsValue) {
      this.chart.options = this.optionsValue
    }
    
    // Redraw the chart
    this.chart.update()
  }
  
  // Method to update chart data manually
  updateData(newData) {
    if (!this.chart) {
      this.dataValue = newData
      this.initializeChart()
      return
    }
    
    this.dataValue = newData
    this.chart.data = newData
    this.chart.update()
  }
  
  // Method to update chart options manually
  updateOptions(newOptions) {
    if (!this.chart) {
      this.optionsValue = newOptions
      this.initializeChart()
      return
    }
    
    this.optionsValue = newOptions
    this.chart.options = newOptions
    this.chart.update()
  }
  
  // Method to resize the chart when its container changes
  resize() {
    if (this.chart) {
      this.chart.resize()
    }
  }
}